import React, { useState } from 'react';
import { PageTracker } from '../../../components/PageTracker.tsx';
import '../../../styles/dev-utils-theme.css';
import { 
  GitBranch, 
  Copy, 
  Check, 
  Search, 
  Terminal,
  BookOpen,
  Star,
  RotateCcw,
  ArrowUpDown,
  Settings,
  Eye
} from 'lucide-react';

interface GitCommand {
  id: string;
  title: string;
  command: string;
  description: string;
  category: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  tags: string[];
  examples?: string[];
  important?: boolean;
}

function GitHelper() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState('all');
  const [copiedCommand, setCopiedCommand] = useState<string | null>(null);
  const [showFavorites, setShowFavorites] = useState(false);
  const [favorites, setFavorites] = useState<string[]>([]);

  const gitCommands: GitCommand[] = [
    // Basic Commands
    {
      id: '1',
      title: 'Initialize Repository',
      command: 'git init',
      description: 'Initialize a new Git repository in the current directory',
      category: 'basic',
      difficulty: 'beginner',
      tags: ['setup', 'init'],
      examples: ['git init', 'git init my-project'],
      important: true
    },
    {
      id: '2',
      title: 'Clone Repository',
      command: 'git clone <url>',
      description: 'Create a local copy of a remote repository',
      category: 'basic',
      difficulty: 'beginner',
      tags: ['clone', 'remote'],
      examples: ['git clone https://github.com/user/repo.git', 'git clone <url> <directory-name>'],
      important: true
    },
    {
      id: '3',
      title: 'Check Status',
      command: 'git status',
      description: 'Show the working directory status and staging area',
      category: 'basic',
      difficulty: 'beginner',
      tags: ['status', 'check'],
      examples: ['git status', 'git status --short'],
      important: true
    },
    {
      id: '4',
      title: 'Add Files to Staging',
      command: 'git add <file>',
      description: 'Add file changes to the staging area',
      category: 'basic',
      difficulty: 'beginner',
      tags: ['add', 'staging'],
      examples: ['git add .', 'git add file.txt', 'git add src/'],
      important: true
    },
    {
      id: '5',
      title: 'Commit Changes',
      command: 'git commit -m "message"',
      description: 'Record changes to the repository with a descriptive message',
      category: 'basic',
      difficulty: 'beginner',
      tags: ['commit', 'save'],
      examples: ['git commit -m "Add new feature"', 'git commit -am "Quick commit"'],
      important: true
    },
    {
      id: '6',
      title: 'Push to Remote',
      command: 'git push origin <branch>',
      description: 'Upload local repository content to a remote repository',
      category: 'basic',
      difficulty: 'beginner',
      tags: ['push', 'remote'],
      examples: ['git push origin main', 'git push -u origin feature-branch'],
      important: true
    },
    {
      id: '7',
      title: 'Pull from Remote',
      command: 'git pull origin <branch>',
      description: 'Fetch and merge changes from the remote repository',
      category: 'basic',
      difficulty: 'beginner',
      tags: ['pull', 'update'],
      examples: ['git pull origin main', 'git pull --rebase'],
      important: true
    },

    // Branching Commands
    {
      id: '8',
      title: 'List Branches',
      command: 'git branch',
      description: 'List all local branches in the repository',
      category: 'branching',
      difficulty: 'beginner',
      tags: ['branch', 'list'],
      examples: ['git branch', 'git branch -a', 'git branch -r']
    },
    {
      id: '9',
      title: 'Create New Branch',
      command: 'git checkout -b <branch-name>',
      description: 'Create and switch to a new branch',
      category: 'branching',
      difficulty: 'beginner',
      tags: ['branch', 'create'],
      examples: ['git checkout -b feature/new-login', 'git checkout -b hotfix/bug-123'],
      important: true
    },
    {
      id: '10',
      title: 'Switch Branch',
      command: 'git checkout <branch-name>',
      description: 'Switch to an existing branch',
      category: 'branching',
      difficulty: 'beginner',
      tags: ['branch', 'switch'],
      examples: ['git checkout main', 'git checkout feature/dashboard']
    },
    {
      id: '11',
      title: 'Merge Branch',
      command: 'git merge <branch-name>',
      description: 'Merge specified branch into the current branch',
      category: 'branching',
      difficulty: 'intermediate',
      tags: ['merge', 'combine'],
      examples: ['git merge feature/new-feature', 'git merge --no-ff develop']
    },
    {
      id: '12',
      title: 'Delete Branch',
      command: 'git branch -d <branch-name>',
      description: 'Delete a local branch (safe delete)',
      category: 'branching',
      difficulty: 'intermediate',
      tags: ['branch', 'delete'],
      examples: ['git branch -d feature/completed', 'git branch -D force-delete']
    },

    // History Commands
    {
      id: '13',
      title: 'View Commit History',
      command: 'git log',
      description: 'Show commit history for the current branch',
      category: 'history',
      difficulty: 'beginner',
      tags: ['log', 'history'],
      examples: ['git log', 'git log --oneline', 'git log --graph --all']
    },
    {
      id: '14',
      title: 'Show Commit Details',
      command: 'git show <commit-hash>',
      description: 'Show detailed information about a specific commit',
      category: 'history',
      difficulty: 'intermediate',
      tags: ['show', 'commit'],
      examples: ['git show HEAD', 'git show abc123']
    },
    {
      id: '15',
      title: 'Compare Changes',
      command: 'git diff',
      description: 'Show differences between working directory and staging area',
      category: 'history',
      difficulty: 'beginner',
      tags: ['diff', 'compare'],
      examples: ['git diff', 'git diff --staged', 'git diff HEAD~1']
    },

    // Undoing Changes
    {
      id: '16',
      title: 'Undo Working Directory Changes',
      command: 'git checkout -- <file>',
      description: 'Discard changes in working directory for specific file',
      category: 'undo',
      difficulty: 'intermediate',
      tags: ['undo', 'discard'],
      examples: ['git checkout -- file.txt', 'git checkout -- .']
    },
    {
      id: '17',
      title: 'Unstage File',
      command: 'git reset HEAD <file>',
      description: 'Remove file from staging area, keep changes in working directory',
      category: 'undo',
      difficulty: 'intermediate',
      tags: ['unstage', 'reset'],
      examples: ['git reset HEAD file.txt', 'git reset HEAD .']
    },
    {
      id: '18',
      title: 'Revert Commit',
      command: 'git revert <commit-hash>',
      description: 'Create a new commit that undoes changes from a previous commit',
      category: 'undo',
      difficulty: 'intermediate',
      tags: ['revert', 'undo'],
      examples: ['git revert HEAD', 'git revert abc123'],
      important: true
    },
    {
      id: '19',
      title: 'Reset to Previous Commit',
      command: 'git reset --hard <commit-hash>',
      description: 'Reset repository to a previous commit (destructive)',
      category: 'undo',
      difficulty: 'advanced',
      tags: ['reset', 'hard'],
      examples: ['git reset --hard HEAD~1', 'git reset --hard abc123']
    },

    // Remote Commands
    {
      id: '20',
      title: 'Add Remote',
      command: 'git remote add origin <url>',
      description: 'Add a remote repository URL',
      category: 'remote',
      difficulty: 'beginner',
      tags: ['remote', 'add'],
      examples: ['git remote add origin https://github.com/user/repo.git']
    },
    {
      id: '21',
      title: 'List Remotes',
      command: 'git remote -v',
      description: 'List all configured remote repositories',
      category: 'remote',
      difficulty: 'beginner',
      tags: ['remote', 'list'],
      examples: ['git remote -v', 'git remote show origin']
    },
    {
      id: '22',
      title: 'Fetch from Remote',
      command: 'git fetch origin',
      description: 'Download objects and refs from remote repository',
      category: 'remote',
      difficulty: 'intermediate',
      tags: ['fetch', 'download'],
      examples: ['git fetch origin', 'git fetch --all']
    },

    // Advanced Commands
    {
      id: '23',
      title: 'Stash Changes',
      command: 'git stash',
      description: 'Temporarily save changes without committing',
      category: 'advanced',
      difficulty: 'intermediate',
      tags: ['stash', 'temporary'],
      examples: ['git stash', 'git stash push -m "work in progress"'],
      important: true
    },
    {
      id: '24',
      title: 'Apply Stash',
      command: 'git stash pop',
      description: 'Apply the most recent stash and remove it from stash list',
      category: 'advanced',
      difficulty: 'intermediate',
      tags: ['stash', 'apply'],
      examples: ['git stash pop', 'git stash apply stash@{0}']
    },
    {
      id: '25',
      title: 'Interactive Rebase',
      command: 'git rebase -i HEAD~<n>',
      description: 'Interactively rewrite commit history',
      category: 'advanced',
      difficulty: 'advanced',
      tags: ['rebase', 'history'],
      examples: ['git rebase -i HEAD~3', 'git rebase -i main']
    },
    {
      id: '26',
      title: 'Cherry Pick',
      command: 'git cherry-pick <commit-hash>',
      description: 'Apply changes from a specific commit to current branch',
      category: 'advanced',
      difficulty: 'advanced',
      tags: ['cherry-pick', 'apply'],
      examples: ['git cherry-pick abc123', 'git cherry-pick feature~2']
    },
    {
      id: '27',
      title: 'Create Tag',
      command: 'git tag -a v1.0 -m "version 1.0"',
      description: 'Create an annotated tag for a specific version',
      category: 'advanced',
      difficulty: 'intermediate',
      tags: ['tag', 'version'],
      examples: ['git tag v1.0', 'git tag -a v1.0 -m "Release version 1.0"']
    },
    {
      id: '28',
      title: 'Blame File',
      command: 'git blame <file>',
      description: 'Show who modified each line of a file',
      category: 'advanced',
      difficulty: 'intermediate',
      tags: ['blame', 'author'],
      examples: ['git blame src/index.js', 'git blame -L 10,20 file.txt']
    }
  ];

  const categories = [
    { value: 'all', label: 'All Categories', icon: BookOpen },
    { value: 'basic', label: 'Basic Commands', icon: Terminal },
    { value: 'branching', label: 'Branching', icon: GitBranch },
    { value: 'history', label: 'History & Logs', icon: Eye },
    { value: 'undo', label: 'Undoing Changes', icon: RotateCcw },
    { value: 'remote', label: 'Remote Operations', icon: ArrowUpDown },
    { value: 'advanced', label: 'Advanced', icon: Settings }
  ];

  const difficulties = [
    { value: 'all', label: 'All Levels' },
    { value: 'beginner', label: 'Beginner', color: 'bg-green-100 text-green-800' },
    { value: 'intermediate', label: 'Intermediate', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'advanced', label: 'Advanced', color: 'bg-red-100 text-red-800' }
  ];

  const filteredCommands = gitCommands.filter(command => {
    const matchesSearch = command.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         command.command.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         command.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         command.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesCategory = selectedCategory === 'all' || command.category === selectedCategory;
    const matchesDifficulty = selectedDifficulty === 'all' || command.difficulty === selectedDifficulty;
    const matchesFavorites = !showFavorites || favorites.includes(command.id);

    return matchesSearch && matchesCategory && matchesDifficulty && matchesFavorites;
  });

  const copyToClipboard = async (command: string, id: string) => {
    try {
      await navigator.clipboard.writeText(command);
      setCopiedCommand(id);
      setTimeout(() => setCopiedCommand(null), 2000);
    } catch (err) {
      console.error('Failed to copy command:', err);
    }
  };

  const toggleFavorite = (commandId: string) => {
    setFavorites(prev => 
      prev.includes(commandId) 
        ? prev.filter(id => id !== commandId)
        : [...prev, commandId]
    );
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'intermediate': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'advanced': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getCategoryIcon = (category: string) => {
    const categoryData = categories.find(cat => cat.value === category);
    const Icon = categoryData?.icon || Terminal;
    return <Icon size={16} />;
  };

  return (
    <div className="dev-utils-page" style={{ minHeight: "100vh", background: "var(--lyra-color-surface-2)", fontFamily: "'DM Sans', system-ui, sans-serif", padding: "28px 28px 48px" }}>
      <PageTracker pageTitle="Git Helper" category="dev-utils" />
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <GitBranch size={20} style={{ color: "var(--app-primary-mid)" }} />
          <span style={{ fontSize: 22, fontWeight: 600, color: "var(--lyra-color-text)", fontFamily: "DM Sans, system-ui, sans-serif" }}>Git Helper</span>
        </div>
        <p style={{ fontSize: 13, color: "var(--app-text-2)", margin: 0 }}>Essential Git commands with descriptions and examples for developers</p>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-6">
        <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search size={20} className="absolute left-3 top-2.5 text-gray-400" />
            <input
              type="text"
              placeholder="Search commands, descriptions, or tags..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            />
          </div>

          {/* Filters */}
          <div className="flex gap-4 items-center flex-wrap">
            {/* Category Filter */}
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-sm"
            >
              {categories.map(category => (
                <option key={category.value} value={category.value}>
                  {category.label}
                </option>
              ))}
            </select>

            {/* Difficulty Filter */}
            <select
              value={selectedDifficulty}
              onChange={(e) => setSelectedDifficulty(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-sm"
            >
              {difficulties.map(difficulty => (
                <option key={difficulty.value} value={difficulty.value}>
                  {difficulty.label}
                </option>
              ))}
            </select>

            {/* Favorites Toggle */}
            <button
              onClick={() => setShowFavorites(!showFavorites)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                showFavorites 
                  ? 'bg-yellow-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              <Star size={18} />
              Favorites ({favorites.length})
            </button>
          </div>
        </div>

        {/* Active Filters */}
        {(selectedCategory !== 'all' || selectedDifficulty !== 'all' || showFavorites) && (
          <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <span className="text-sm text-gray-600 dark:text-gray-400">Active filters:</span>
            {selectedCategory !== 'all' && (
              <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                {categories.find(c => c.value === selectedCategory)?.label}
              </span>
            )}
            {selectedDifficulty !== 'all' && (
              <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">
                {difficulties.find(d => d.value === selectedDifficulty)?.label}
              </span>
            )}
            {showFavorites && (
              <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs">
                Favorites Only
              </span>
            )}
            <button
              onClick={() => {
                setSelectedCategory('all');
                setSelectedDifficulty('all');
                setShowFavorites(false);
              }}
              className="px-2 py-1 bg-gray-100 text-gray-600 hover:bg-gray-200 rounded text-xs transition-colors"
            >
              Clear All
            </button>
          </div>
        )}
      </div>

      {/* Commands Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredCommands.map(command => (
          <div
            key={command.id}
            className={`bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 hover:shadow-xl transition-all duration-200 ${
              command.important ? 'ring-2 ring-blue-200 dark:ring-blue-800' : ''
            }`}
          >
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-start gap-3 flex-1">
                <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                  {getCategoryIcon(command.category)}
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 dark:text-white text-lg leading-tight">
                    {command.title}
                    {command.important && (
                      <Star size={16} className="inline ml-2 text-yellow-500 fill-current" />
                    )}
                  </h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getDifficultyColor(command.difficulty)}`}>
                      {command.difficulty}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                      {command.category}
                    </span>
                  </div>
                </div>
              </div>
              
              <button
                onClick={() => toggleFavorite(command.id)}
                className={`p-1 rounded transition-colors ${
                  favorites.includes(command.id)
                    ? 'text-yellow-500 hover:text-yellow-600'
                    : 'text-gray-400 hover:text-yellow-500'
                }`}
              >
                <Star size={18} className={favorites.includes(command.id) ? 'fill-current' : ''} />
              </button>
            </div>

            {/* Command */}
            <div className="mb-4">
              <div className="relative bg-gray-900 dark:bg-gray-950 rounded-lg p-3">
                <code className="text-green-400 font-mono text-sm">
                  $ {command.command}
                </code>
                <button
                  onClick={() => copyToClipboard(command.command, command.id)}
                  className={`absolute top-2 right-2 p-1 rounded transition-colors ${
                    copiedCommand === command.id
                      ? 'text-green-400'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  {copiedCommand === command.id ? <Check size={16} /> : <Copy size={16} />}
                </button>
              </div>
            </div>

            {/* Description */}
            <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 leading-relaxed">
              {command.description}
            </p>

            {/* Examples */}
            {command.examples && command.examples.length > 0 && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Examples:</h4>
                <div className="space-y-2">
                  {command.examples.map((example, index) => (
                    <div key={index} className="relative bg-gray-50 dark:bg-gray-700 rounded p-2">
                      <code className="text-xs font-mono text-gray-800 dark:text-gray-200">
                        $ {example}
                      </code>
                      <button
                        onClick={() => copyToClipboard(example, `${command.id}-example-${index}`)}
                        className={`absolute top-1 right-1 p-1 rounded transition-colors ${
                          copiedCommand === `${command.id}-example-${index}`
                            ? 'text-green-600'
                            : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                        }`}
                      >
                        {copiedCommand === `${command.id}-example-${index}` ? 
                          <Check size={12} /> : <Copy size={12} />
                        }
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tags */}
            <div className="flex flex-wrap gap-1">
              {command.tags.map(tag => (
                <span
                  key={tag}
                  className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded text-xs"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* No Results */}
      {filteredCommands.length === 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-12 text-center">
          <GitBranch size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No commands found</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Try adjusting your search terms or filters
          </p>
          <button
            onClick={() => {
              setSearchTerm('');
              setSelectedCategory('all');
              setSelectedDifficulty('all');
              setShowFavorites(false);
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Clear All Filters
          </button>
        </div>
      )}

      {/* Stats */}
      <div className="mt-8 bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Command Statistics</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
              {gitCommands.length}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Total Commands</div>
          </div>
          <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
              {gitCommands.filter(c => c.difficulty === 'beginner').length}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Beginner</div>
          </div>
          <div className="text-center p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
            <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
              {gitCommands.filter(c => c.difficulty === 'intermediate').length}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Intermediate</div>
          </div>
          <div className="text-center p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
            <div className="text-2xl font-bold text-red-600 dark:text-red-400">
              {gitCommands.filter(c => c.difficulty === 'advanced').length}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Advanced</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GitHelper;
